// ----------------------------------------------------------------------
// <copyright file="Izenda.GaugeYAxisMetrics.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * GaugeYAxisMetrics component
 */

import m from 'mobx';
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import Metrics from 'share/components/controls/Izenda.Metrics';
import GaugeValueMetricItem from './Izenda.GaugeValueMetricItem';
import { ContainerName } from 'logic/data/Izenda.ReportPartGaugeConstants';
import { compact, size, pick, assign, head, filter, find, get, isNil } from 'lodash/fp';

@observer
export default class GaugeYAxisMetrics extends Component {
  componentWillMount() {
    this.updateState();
  }

  componentWillReceiveProps() {
    this.updateState();
  }

  componentWillUpdate() {
    this.updateContainerElement();
  }

  render() {
    const props = this.props;
    const state = this.state;
    const type = props.type;
    const reportPart = props.reportPart;
    let itemProps = pick(['reportPart', 'store'], props);
    const dataSource = state.dataSource;
    const container = reportPart.reportPartContent && reportPart.reportPartContent.values;
    itemProps = assign(itemProps, {
      onDeleteElement: handleDeleteElement.bind(this),
      onAddElement: handleAddElement.bind(this),
      onUpdateElement: handleUpdateElement.bind(this),
      type,
      canDelete: dataSource.length > 1
    });

    return (
      <Metrics
        maximumMetrics={props.maximumField}
        dataSource={dataSource}
        onAddMetric={onAddMetric.bind(this)}
        onDeleteMetric={onDeleteMetric.bind(this)}
        itemComponent={GaugeValueMetricItem}
        {...itemProps}
      />
    );

    function handleDeleteElement(index, valueElement, containerName, deleteElement) {
      // We need to remove element from the values container first
      // otherwise the field will have been shown until "Update results"
      // won't be pressed.

      // containerName, deleteElement - are optional
      if (!isNil(deleteElement)) {
        if (containerName === ContainerName.SCALE)
          this.deleteScaleElement(valueElement, deleteElement, false);
      } else {
        this.deleteValuesElement(valueElement, false);
        // clean up state object
        this.state.dataSource[index] = null;
        this.setState({
          dataSource: this.state.dataSource
        });
      }
    }

    function handleAddElement(index, field, querySource, addedField) {
      this.state.dataSource[index] = field;
      this.setState({
        dataSource: this.state.dataSource
      });
    }

    function handleUpdateElement(element, index) {
      container.elements[index] = m.toJS(element);
    }

    function onAddMetric() {
      this.state.dataSource.push(null);
      this.setState({
        dataSource: this.state.dataSource
      });
    }

    function onDeleteMetric(index) {
      this.state.dataSource.splice(index, 1);
      this.setState({
        dataSource: this.state.dataSource
      });
    }
  }

  /**
   * Remove element from values container
   * @param {ReportPartElement} valueElement
   * @param {boolean} updatePreview whether need to update preview or not.
   */
  deleteValuesElement(valueElement, updatePreview) {
    if (!valueElement)
      return;
    const { reportPart, store } = this.props;
    const container = get('reportPartContent.values', reportPart);
    const foundElement = find(f => f.field.fieldNameAlias === valueElement.field.fieldNameAlias, container.elements);
    if (!foundElement)
      return;
    reportPart.removeFieldFromContainer(container, foundElement);
    store.reportFilters.removeAggregatedField(foundElement);

    updatePreview && reportPart.shouldUpdatePreview && store.needUpdatePreview(reportPart);
  }

  deleteScaleElement(valueElement, deletedElement, updatePreview) {
    if (!valueElement || !deletedElement)
      return;
    const { reportPart, store } = this.props;

    // Find scale element
    const container = find({name: ContainerName.SCALE}, reportPart.reportPartContent.containerCollection);
    const foundElement = find(f => f.field.fieldNameAlias === deletedElement.field.fieldNameAlias, container.elements);
    if (!foundElement)
      return;

    // Clean up value field props
    const scaleProperties = get('properties.metric.scale', valueElement);
    if (!isNil(scaleProperties)) {
      if (scaleProperties.fromElement === deletedElement) {
        scaleProperties.fromElement = null;
      } else if (scaleProperties.toElement === deletedElement) {
        scaleProperties.toElement = null;
      }
    }

    // Remove from container and update
    reportPart.removeFieldFromContainer(container, foundElement);
    store.reportFilters.removeAggregatedField(foundElement);
    updatePreview && reportPart.shouldUpdatePreview && store.needUpdatePreview(reportPart);
  }

  /**
   * Update container
   */
  updateContainerElement() {
    const props = this.props;
    const reportPart = props.reportPart;
    const newDataSource = compact(this.state.dataSource);
    const valueElements = reportPart.reportPartContent.values.elements;
    const numNewValues = size(newDataSource);
    const numValues = size(valueElements);

    if (numNewValues === numValues) {
      return;
    }

    // Check for removing elements. If some elements were removed, we need to delete them from values container
    if (numNewValues < numValues) {
      const deletedElement = head(filter(elm => !newDataSource.includes(elm), valueElements));
      if (deletedElement) {
        this.deleteValuesElement(deletedElement, true);
      }
    } else {
      reportPart.reportPartContent.values.elements = newDataSource;
    }
  }

  updateState() {
    const reportPart = this.props.reportPart;
    const container = reportPart.reportPartContent && reportPart.reportPartContent.values;
    const dataSource = [];

    container.elements.forEach(field => {
      dataSource.push(field);
    });

    // at least one metric is showed
    this.setState({
      dataSource: dataSource.length ? dataSource : [null]
    });
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/FieldContainers/Izenda.GaugeYAxisMetrics.js